import { getCountry, getLocale } from '@js/App/Utils/CountryAndLocale'
import { getFromCache, isInCache, putInCache } from '@js/App/Performance/LocalCache'
import { usePage } from '@inertiajs/vue3'
import { trans } from '@js/App/Plugins/Translations'
import { isSSR } from '@js/App/Utils/Environment'
import * as Sentry from '@sentry/vue'

/**
 * fetch() rejects instead of resolving when the request never produced a response at all:
 * offline, DNS/TLS failure, CORS rejection, or the browser aborting in-flight requests
 * because the user navigated away. There is no status code in that case, so callers get 0.
 * @returns {{hasErrors: boolean, status: number, errors: {general: string}}}
 */
const getNetworkErrorResponse = () => ({
  hasErrors: true,
  status: 0,
  errors: {
    general: trans('Errors.General')
  }
})

/**
 * Performs the request and normalises every outcome - transport failure, HTTP error status,
 * unparseable body - into the same { hasErrors } shape callers already branch on. Without
 * this an offline or aborted fetch escapes as an unhandled rejection, and any loading counter
 * the caller incremented before awaiting is never decremented.
 * @returns {Promise<{hasErrors: boolean, status?: Number, errors?: {}, json?: {}}>}
 */
const doRequest = async (url, options) => {
  let response

  try {
    response = await fetch(url, options)
  } catch (e) {
    // A breadcrumb, not captureException: the majority of these are the browser aborting
    // in-flight requests because the user navigated away, which is indistinguishable from a
    // real failure at this layer (Safari reports both as "Load failed"). Capturing would
    // just re-create the unhandled-rejection noise this catch exists to remove, while a
    // breadcrumb still shows the failed request underneath whatever error does get reported.
    Sentry.addBreadcrumb({ category: 'fetch', level: 'warning', message: `${options?.method || 'GET'} ${url} failed at the transport layer` })
    return getNetworkErrorResponse()
  }

  if (response.status >= 400) {
    return {
      hasErrors: true,
      status: response.status,
      errors: await getApiErrorMessageFromResponse(response)
    }
  }

  try {
    return {
      hasErrors: false,
      json: await response.json()
    }
  } catch (e) {
    return getNetworkErrorResponse()
  }
}

export const doGetRequest = async (url, params = {}, cacheResponse = false) => {
  if (!isSSR() && cacheResponse && isInCache({ method: 'GET', url, params })) {
    return getFromCache({ method: 'GET', url, params })
  }

  const responseObject = await doRequest(url + '?' + new URLSearchParams(params), {
    headers: {
      Accept: 'application/json',
      ...getDefaultHeadersToSend()
    }
  })

  if (!responseObject.hasErrors && !isSSR() && cacheResponse) {
    putInCache({ method: 'GET', url, params }, responseObject)
  }

  return responseObject
}

export const doPostRequest = async (url, postData, cacheResponse = false) => {
  if (!isSSR() && cacheResponse && isInCache({ method: 'POST', url, postData })) {
    return getFromCache({ method: 'POST', url, postData })
  }

  const responseObject = await doRequest(url, {
    method: 'POST',
    body: JSON.stringify(postData),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getDefaultHeadersToSend()
    }
  })

  if (!responseObject.hasErrors && !isSSR() && cacheResponse) {
    putInCache({ method: 'POST', url, postData }, responseObject)
  }

  return responseObject
}

export const doDeleteRequest = async (url, cacheResponse = false) => {
  if (!isSSR() && cacheResponse && isInCache({ method: 'DELETE', url })) {
    return getFromCache({ method: 'DELETE', url })
  }

  const responseObject = await doRequest(url, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      ...getDefaultHeadersToSend()
    }
  })

  if (!responseObject.hasErrors && !isSSR() && cacheResponse) {
    putInCache({ method: 'DELETE', url }, responseObject)
  }

  return responseObject
}

/**
 * Returns the default headers to be added to every request
 * @returns {{"X-Locale": String, "X-Country": String}}
 */
export const getDefaultHeadersToSend = () => {
  const page = usePage()
  return {
    'X-Locale': getLocale(),
    'X-Country': getCountry(),
    'X-CSRF-TOKEN': page.props.csrf
  }
}

/**
 * Converts an API exception to a translated error object.
 * @param response The fetch response object
 * @returns {{}|{general: string}} An object containing the error values. For example:
 * {
 *     "email": "Please provide a valid email",
 *     "password": "The password must be 8 characters."
 * }
 */
export const getApiErrorMessageFromResponse = async (response) => {
  const errorObject = {}

  const asJson = await response.json().catch(() => null)

  const errors = asJson?.errors
  if (!errors) {
    return {
      general: trans('Errors.General')
    }
  }

  for (const key of Object.keys(errors)) {
    errorObject[key] = errors[key][0]
  }

  return errorObject
}
